// Lazy loader for the YouTube IFrame Player API, plus the minimal typings we use.
//
// The API is a single global script that calls `window.onYouTubeIframeAPIReady` exactly once when
// ready. We load it on demand (only when a module actually has a video), memoize the load as one
// shared promise, and chain onto any pre-existing ready handler rather than clobbering it — so
// multiple players/callers can coexist safely.

export interface YouTubePlayer {
  destroy(): void;
}

export interface YouTubePlayerEvent {
  data: number;
  target: YouTubePlayer;
}

interface YouTubePlayerOptions {
  events?: {
    onReady?: (event: YouTubePlayerEvent) => void;
    onStateChange?: (event: YouTubePlayerEvent) => void;
  };
}

export interface YouTubeApi {
  Player: new (element: HTMLElement | string, options: YouTubePlayerOptions) => YouTubePlayer;
  PlayerState: {
    UNSTARTED: number;
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
    BUFFERING: number;
    CUED: number;
  };
}

declare global {
  interface Window {
    YT?: YouTubeApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';
let apiPromise: Promise<YouTubeApi> | null = null;

/** Load (once) and resolve the global YouTube IFrame API. */
export function loadYouTubeIframeApi(): Promise<YouTubeApi> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('YouTube IFrame API is unavailable outside the browser'));
  }
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<YouTubeApi>((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      if (window.YT?.Player) resolve(window.YT);
      else {
        // Don't memoize this failure: a partial load / privacy stub that fired the ready handler
        // without a Player constructor shouldn't poison every later caller for the whole session.
        apiPromise = null; // let a later caller retry
        reject(new Error('YouTube IFrame API loaded without a Player constructor'));
      }
    };

    if (!document.querySelector(`script[src="${IFRAME_API_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = IFRAME_API_SRC;
      script.async = true;
      script.onerror = () => {
        // Remove the dead tag too — the querySelector guard above would otherwise see it and
        // skip the append, leaving every retry waiting on a script that will never load.
        script.remove();
        apiPromise = null; // let a later caller retry
        reject(new Error('Failed to load the YouTube IFrame API'));
      };
      document.head.appendChild(script);
    }
  });

  return apiPromise;
}
